
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { servicesData } from '../data/servicesData';
import { saveServiceSubmission } from '../utils/serviceFormStorage';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import PageHeader from '../components/PageHeader';
import background4 from '../assets/background-4.jpg';
import '../css/serviceDetail.css';

const ServiceDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [service, setService] = useState(null);
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    
    // Signature Canvas Ref
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        const foundService = servicesData.find(s => s.slug === slug);
        if (foundService) {
            // Filter out zipCode and region fields
            const filteredFields = foundService.formFields.filter(f => 
                f.name !== 'zipCode' && 
                f.name !== 'workZipCode' && 
                f.name !== 'region' && 
                f.name !== 'workRegion' &&
                f.name !== 'homeZipCode' &&
                f.name !== 'officeZipCode'
            ).map(f => {
                // 1. Contractors form: Change select to text for contractType field
                if (slug === 'contractors-form' && f.name === 'contractType') {
                    return {
                        ...f,
                        type: 'text'
                    };
                }

                // 2. General Data Collection Form: Remove "choose one of these"
                if (slug === 'general-data-collection-form' && f.label === 'Choose one of these') {
                    return null; // Will be filtered out
                }

                // 3. Fix "select select" in labels
                let newLabel = f.label || '';
                if (newLabel.toLowerCase().includes('select select')) {
                    newLabel = newLabel.replace(/select select/gi, 'select');
                }

                // Normalize labels:
                // - Keep sub-category wording exactly as requested
                // - Use category wording for professionCategory
                if (f.name === 'professionSubCategory') {
                    newLabel = "Select a sub-category that match your specialization";
                } else if (f.name === 'professionCategory' || f.label === "Select a category that best fit your profession") {
                    newLabel = "Select a category that best fit your profession";
                }
                
                // Filter out "Others (specify below)" options entirely and update country names in options
                let newOptions = f.options;
                if (f.options && Array.isArray(f.options)) {
                    // Filter out any option that contains "specify below" (case insensitive)
                    newOptions = f.options.filter(opt => !opt.toLowerCase().includes('specify below') && !opt.toLowerCase().includes('(specify) below'));
                    
                    // Replace "Republic of the Yoruba" with "Democratic Republic of the Yoruba" in any option list
                    newOptions = newOptions.map(opt => {
                        if (opt === "Republic of the Yoruba") return "Democratic Republic of the Yoruba";
                        return opt;
                    });
                }

                // Remove "Choose a job that suits you" from Artisans AND Health and Wellness
                if ((slug === 'artisans' || slug === 'health-and-wellness-plan-health-matters') && f.name === 'jobSuitability') {
                    return null;
                }
                
                // 4. Engineering form: Add "Other" to profession category
                if (slug === 'engineering' && f.name === 'professionCategory') {
                    const options = [...(f.options || [])];
                    if (!options.includes('Other')) {
                        options.push('Other');
                    }
                    return {
                        ...f,
                        label: newLabel,
                        options: options
                    };
                }

                // Update Title options (Existing logic)
                if (f.name === 'title' && f.type === 'select') {
                    return {
                        ...f,
                        label: newLabel,
                        options: ["Mr", "Mrs", "Ms", "Dr", "Prof", "Other"]
                    };
                }
                // Update Country options (Existing logic)
                if ((f.name === 'country' || f.name === 'workCountry' || f.name === 'homeCountry' || f.name === 'officeCountry' || f.name === 'insuranceOfficeCountry') && f.type === 'select') {
                    const countries = (f.options || []).filter(c => c !== "Nigeria" && !c.includes("Nigeria") && c !== "Republic of the Yoruba");
                    if (!countries.includes("Democratic Republic of the Yoruba")) {
                        countries.unshift("Democratic Republic of the Yoruba");
                    }
                    return {
                        ...f,
                        label: newLabel,
                        options: countries
                    };
                }
                // Update Agreement text (Existing logic)
                if (f.name === 'agreement' && f.type === 'checkbox') {
                    return {
                        ...f,
                        label: "I have read and agree to the rules and policies on the website of the Democratic Republic of the Yoruba."
                    };
                }
                return {
                    ...f,
                    label: newLabel,
                    options: newOptions
                };
            }).filter(f => f !== null);

            setService({
                ...foundService,
                formFields: filteredFields
            });

            // Initialize form data
            const initialData = {};
            filteredFields.forEach(field => {
                if (field.type !== 'header' && field.type !== 'paragraph') {
                    initialData[field.name] = '';
                }
            });
            setFormData(initialData);
        } else {
            navigate('/');
        }
    }, [slug, navigate]);

    // Canvas Signature Handlers
    const startDrawing = (e) => {
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches[0].clientX) - rect.left;
        const y = (e.clientY || e.touches[0].clientY) - rect.top;
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const rect = canvas.getBoundingClientRect();
        const x = (e.clientX || e.touches[0].clientX) - rect.left;
        const y = (e.clientY || e.touches[0].clientY) - rect.top;
        ctx.lineTo(x, y);
        ctx.stroke();
        
        // Save canvas as data URL to form state
        setFormData(prev => ({
            ...prev,
            signature: canvas.toDataURL()
        }));
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setFormData(prev => ({
            ...prev,
            signature: ''
        }));
    };

    const handleInputChange = async (e) => {
        const { name, value, type, files, checked } = e.target;
        
        if (type === 'file') {
            const file = files[0];
            if (file) {
                // Convert to Base64 for storage
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFormData(prev => ({
                        ...prev,
                        [name]: {
                            name: file.name,
                            type: file.type,
                            data: reader.result
                        }
                    }));
                };
                reader.readAsDataURL(file);
            }
        } else if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess(false);

        try {
            const submission = {
                serviceId: service.id,
                serviceTitle: service.title,
                formData: formData
            };
            await saveServiceSubmission(submission);
            setSuccess(true);
            setFormData(prev => {
                const reset = {};
                // Reset form data but keep structure for controlled inputs
                service.formFields.forEach(field => {
                     if (field.type !== 'header') {
                        reset[field.name] = '';
                     }
                });
                return reset;
            });
            // Reset file inputs manually if needed
            document.querySelectorAll('input[type="file"]').forEach(input => input.value = '');
        } catch (err) {
            console.error(err);
            setError('Failed to submit form. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!service) return <div className="loading-spinner">Loading...</div>;

    const getIconForField = (name, type) => {
        if (type === 'date') return 'fa-calendar-alt';
        if (type === 'file') return 'fa-file-upload';
        if (name.toLowerCase().includes('email')) return 'fa-envelope';
        if (name.toLowerCase().includes('phone') || name.toLowerCase().includes('tel') || name.toLowerCase().includes('mobile')) return 'fa-phone';
        if (name.toLowerCase().includes('name') || name.toLowerCase().includes('user') || name.toLowerCase().includes('kin')) return 'fa-user';
        if (name.toLowerCase().includes('address') || name.toLowerCase().includes('city') || name.toLowerCase().includes('country') || name.toLowerCase().includes('zip')) return 'fa-map-marker-alt';
        if (name.toLowerCase().includes('company') || name.toLowerCase().includes('institution') || name.toLowerCase().includes('hospital')) return 'fa-building';
        if (name.toLowerCase().includes('message') || name.toLowerCase().includes('proposal') || name.toLowerCase().includes('explanation')) return 'fa-comment-alt';
        if (type === 'url' || name.toLowerCase().includes('link') || name.toLowerCase().includes('portfolio')) return 'fa-link';
        if (type === 'select') return 'fa-list-ul';
        return 'fa-pen';
    };

    return (
        <>
            <Navbar />
            <div className="service-detail-page">
                <PageHeader title={service.title} path={`Services / ${service.title}`} bgImage={background4} />

                <div className="container my-5">
                    <div className="row">
                        {/* Left Content Column */}
                        <div className="col-lg-7 mb-5 mb-lg-0">
                            <div className="service-content-wrapper">
                                {service.content.map((block, index) => {
                                    if (block.type === 'heading') {
                                        return <h2 key={index} className="service-heading">{block.text}</h2>;
                                    }
                                    if (block.type === 'paragraph') {
                                        return <p key={index} className="service-paragraph">{block.text}</p>;
                                    }
                                    if (block.type === 'list') {
                                        return (
                                            <ul key={index} className="service-list">
                                                {block.items.map((item, i) => (
                                                    <li key={i}><i className="fas fa-check-circle me-2 text-primary"></i>{item}</li>
                                                ))}
                                            </ul>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        </div>

                        {/* Right Form Column */}
                        <div className="col-lg-5">
                            <div className="service-form-card">
                                
                                {success && (
                                    <div className="alert alert-success">
                                        <i className="fas fa-check-circle me-2"></i>
                                        Your submission has been received successfully!
                                    </div>
                                )}
                                {error && (
                                    <div className="alert alert-danger">
                                        <i className="fas fa-exclamation-circle me-2"></i>
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    {service.formFields.map((field, index) => {
                                        if (field.type === 'header') {
                                            return (
                                                <h5 key={index} className="form-section-header mt-4 mb-3 border-bottom pb-2" style={{color: '#002147', fontWeight: 'bold'}}>
                                                    {field.label}
                                                </h5>
                                            );
                                        }

                                        if (field.type === 'paragraph') {
                                             return (
                                                 <p key={index} className="text-muted mb-4" style={{fontSize: '0.9rem'}}>
                                                     {field.text}
                                                 </p>
                                             );
                                        }

                                        if (field.type === 'radio') {
                                            return (
                                                <div key={index} className="form-group mb-3">
                                                    <label className="form-label">
                                                        {field.label} {field.required && <span className="text-danger">*</span>}
                                                    </label>
                                                    <div className="d-flex flex-column gap-2">
                                                        {field.options.map((opt, i) => (
                                                            <div className="form-check" key={i}>
                                                                <input
                                                                    className="form-check-input"
                                                                    type="radio"
                                                                    name={field.name}
                                                                    id={`${field.name}-${i}`}
                                                                    value={opt}
                                                                    checked={formData[field.name] === opt}
                                                                    onChange={handleInputChange}
                                                                    required={field.required}
                                                                />
                                                                <label className="form-check-label" htmlFor={`${field.name}-${i}`}>
                                                                    {opt}
                                                                </label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        }

                                        if (field.type === 'checkbox') {
                                            return (
                                                <div key={index} className="form-check mb-3">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        id={field.name}
                                                        name={field.name}
                                                        checked={formData[field.name] || false}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                    />
                                                    <label className="form-check-label" htmlFor={field.name}>
                                                        {field.label} {field.required && <span className="text-danger">*</span>}
                                                    </label>
                                                </div>
                                            );
                                        }

                                        if (field.name && field.name.toLowerCase().includes('signature')) {
                                            return (
                                                <div key={index} className="form-group mb-3">
                                                    <label className="form-label">
                                                        {field.label} {field.required && <span className="text-danger">*</span>}
                                                    </label>
                                                    <div className="signature-container" style={{ border: '1px solid #ddd', borderRadius: '4px', background: '#fff', position: 'relative' }}>
                                                        <canvas
                                                            ref={canvasRef}
                                                            width={500}
                                                            height={150}
                                                            onMouseDown={startDrawing}
                                                            onMouseMove={draw}
                                                            onMouseUp={stopDrawing}
                                                            onMouseOut={stopDrawing}
                                                            onTouchStart={startDrawing}
                                                            onTouchMove={draw}
                                                            onTouchEnd={stopDrawing}
                                                            style={{ cursor: 'crosshair', width: '100%', height: '150px', touchAction: 'none' }}
                                                        />
                                                        <button 
                                                            type="button" 
                                                            className="btn btn-sm btn-outline-danger" 
                                                            onClick={clearSignature}
                                                            style={{ position: 'absolute', top: '5px', right: '5px', fontSize: '0.7rem' }}
                                                        >
                                                            Clear
                                                        </button>
                                                    </div>
                                                    <p className="text-muted" style={{fontSize: '0.75rem', marginTop: '5px'}}>Draw your signature using mouse or touch.</p>
                                                </div>
                                            );
                                        }

                                        return (
                                        <div key={index} className="form-group mb-3">
                                            <label htmlFor={field.name} className="form-label">
                                                {field.label} {field.required && <span className="text-danger">*</span>}
                                            </label>
                                            
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0">
                                                    <i className={`fas ${getIconForField(field.name, field.type)} text-muted`}></i>
                                                </span>
                                                {field.type === 'textarea' ? (
                                                    <textarea
                                                        className="form-control custom-input border-start-0"
                                                        id={field.name}
                                                        name={field.name}
                                                        rows="4"
                                                        value={formData[field.name] || ''}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                        placeholder={field.placeholder || `Enter your ${field.label.toLowerCase()}`}
                                                    ></textarea>
                                                ) : field.type === 'select' ? (
                                                    <select
                                                        className="form-control custom-input border-start-0"
                                                        id={field.name}
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                    >
                                                        <option value="">{field.placeholder || (field.label.toLowerCase().startsWith('select') ? field.label : `Select ${field.label}`)}</option>
                                                        {field.options.map((opt, i) => (
                                                            <option key={i} value={opt}>{opt}</option>
                                                        ))}
                                                    </select>
                                                ) : field.type === 'file' ? (
                                                    <input
                                                        type="file"
                                                        className="form-control custom-input border-start-0"
                                                        id={field.name}
                                                        name={field.name}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                        accept={field.accept || "*"}
                                                    />
                                                ) : (
                                                    <input
                                                        type={field.type}
                                                        className="form-control custom-input border-start-0"
                                                        id={field.name}
                                                        name={field.name}
                                                        value={formData[field.name] || ''}
                                                        onChange={handleInputChange}
                                                        required={field.required}
                                                        placeholder={field.placeholder || `Enter your ${field.label.toLowerCase()}`}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )})}

                                    <button type="submit" className="btn btn-primary w-100 submit-btn" disabled={loading}>
                                        {loading ? (
                                            <>
                                                <i className="fas fa-spinner fa-spin me-2"></i> Submitting...
                                            </>
                                        ) : (
                                            <>
                                                Submit Request <i className="fas fa-paper-plane ms-2"></i>
                                            </>
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
};

export default ServiceDetail;
